import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { commentsApi, CreateCommentData, UpdateCommentData } from '@/lib/api';
import type { TaskComment } from '@/types';

export function useComments(taskId: string | undefined) {
  return useQuery({
    queryKey: ['comments', taskId],
    queryFn: async () => {
      if (!taskId) throw new Error('Task ID required');
      const response = await commentsApi.getByTask(taskId);
      return response.data as TaskComment[];
    },
    enabled: !!taskId,
  });
}

export function useCreateComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      data,
    }: {
      taskId: string;
      data: CreateCommentData;
    }) => {
      const response = await commentsApi.create(taskId, data);
      return response.data as TaskComment;
    },
    onSuccess: (createdComment, variables) => {
      queryClient.setQueryData<TaskComment[]>(['comments', variables.taskId], (old) => {
        if (!old) return [createdComment];
        if (old.some((c) => c.id === createdComment.id)) return old;
        return [...old, createdComment];
      });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', variables.taskId] });
    },
  });
}

export function useUpdateComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      taskId: string;
      data: UpdateCommentData;
    }) => {
      const response = await commentsApi.update(id, data);
      return response.data as TaskComment;
    },
    onSuccess: (updatedComment, variables) => {
      queryClient.setQueryData<TaskComment[]>(['comments', variables.taskId], (old) => {
        if (!old) return old;
        return old.map((c) => (c.id === variables.id ? updatedComment : c));
      });
    },
  });
}

export function useDeleteComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string; taskId: string }) => {
      await commentsApi.delete(id);
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData<TaskComment[]>(['comments', variables.taskId], (old) => {
        if (!old) return old;
        return old.filter((c) => c.id !== variables.id);
      });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', variables.taskId] });
    },
  });
}
